/**
 * The vendor debug surface — model only, no DOM.
 *
 * DATA-DRIVEN, and that is the whole point. The panel renders whatever
 * `fleet/debug_manifest` says, so a call added to the adapter's registry appears here
 * with no change to this app, and one a vendor constraint disables greys out WITH ITS
 * REASON instead of failing when pressed. Nothing below hard-codes a call, a parameter
 * or an endpoint.
 *
 * Correlation is by `request_id` in the payload, never a per-request topic: MQTT 5
 * response topics are unavailable end to end (only this app negotiates v5), and one
 * channel means two operators watching a site both see what was asked.
 */
import type { Connection } from "./connection";
import { fleetTopic, type ChannelName } from "./contract/fleetComms";

export interface ParamSchema {
  type?: string;
  title?: string;
  /** The value used when the operator supplies none. Also decides whether a field is
   *  required: the adapter treats a parameter with a default as optional. */
  default?: unknown;
  /** A hint, never a contract: an unknown one degrades to a plain input. */
  "x-ui"?: string;
  pattern?: string;
  maxLength?: number;
  minimum?: number;
  maximum?: number;
  enum?: Array<string | number>;
  /** Human labels for an `enum`'s values, keyed by the value as a string. A hint, like
   *  `x-ui`: it changes what a dropdown READS, never what is accepted. */
  "x-labels"?: Record<string, string>;
}

export interface DebugCall {
  name: string;
  title?: string;
  /** What the endpoint returns, in the VENDOR's field names. */
  doc?: string;
  group?: string;
  scope?: string;
  enabled: boolean;
  reason?: string;
  min_interval_ms?: number;
  /**
   * How long an answer stays good for, DECLARED by the adapter. Absent means the adapter
   * predates caching, and an unrecognised tier means the same — in both cases nothing may
   * be assumed about freshness, because serving data under a policy we do not understand
   * is worse than one more vendor call.
   *
   * `ttl_ms` is resolved adapter-side precisely so this app carries no tier→ms table of
   * its own; that would be a second declaration of the registry.
   */
  cache?: { tier?: string; ttl_ms?: number };
  result?: string;
  params?: Record<string, ParamSchema>;
  required?: string[];
}

export interface DebugManifest {
  schema_version?: number | string;
  adapter?: { name?: string; version?: string };
  calls: DebugCall[];
}

export interface DebugResult {
  request_id: string;
  call?: string;
  /** The vendor endpoint this answer came from. Present on results, never on the
   *  manifest — see the adapter's debug.py for why the two differ. */
  path?: string;
  /** The parameters actually SENT: post-validation, post-defaults, blanks dropped. */
  params?: Record<string, unknown>;
  ts: number;
  ok: boolean;
  elapsed_ms?: number;
  /** True when the answer came from the broker-retained cache, not the vendor. STATED by
   *  the adapter rather than inferred from how fast it arrived: "fast" and "cached" are
   *  different facts, and only one of them means the reading might be a day old. */
  cached?: boolean;
  /**
   * For a call whose result is `artifact_ref`: WHERE the bytes are, instead of the
   * bytes. A map image is megabytes and would not fit on this channel; `data` still
   * carries the vendor's metadata with the binary field removed.
   *
   * `url` is absent when the adapter holds an image it cannot serve — it has no public
   * base URL configured — and `error` then says so. That is deliberately different from
   * a link that does not work.
   */
  artifact?: {
    url?: string;
    sha256?: string;
    bytes?: number;
    content_type?: string;
    image?: { width?: number; height?: number; mode?: string };
    expires_at?: number;
    error?: string;
  };
  /** Age of that cached answer, in ms, AT THE MOMENT IT WAS SERVED. A fixed fact about
   *  this answer, not a live counter — which is why it can be rendered once and left
   *  alone, and must never enter a paint signature. */
  age_ms?: number;
  error?: string;
  data?: unknown;
  vendor_code?: number | string;
}

/** One in-flight or finished query, per site. */
export interface Pending {
  requestId: string;
  call: string;
  siteId: string;
  /** Set when the query was addressed to one robot, so a detail view can show only its
   *  own. A shared channel carries every operator's questions; showing another robot's
   *  answer under this one would be actively misleading. */
  robotId?: string;
  sentAt: number;
  /** Set when the operator pressed `refresh`, so the log can say a re-ask was deliberate
   *  rather than leave two identical-looking rows. */
  forced?: boolean;
  result?: DebugResult;
}

/**
 * One cached vendor answer, as it sits RETAINED on the broker.
 *
 * These arrive on subscribe without anyone asking — which is the whole point of putting
 * the cache there. A panel opening cold already holds the store list, so the first
 * screen can render with zero vendor calls behind it.
 */
export interface CacheRecord {
  siteId: string;
  entryId: string;
  call: string;
  params?: Record<string, unknown>;
  data?: unknown;
  stored_at: number;
  ttl_ms: number;
  tier?: string;
}

export interface DebugState {
  /** siteId -> its adapter's manifest. Retained, so it arrives on subscribe. */
  manifests: Map<string, DebugManifest>;
  /** `siteId|entryId` -> the retained answer. Also arrives on subscribe. */
  cache: Map<string, CacheRecord>;
  /** Newest first. Bounded — this is a debug log, not a history. */
  log: Pending[];
}

export const LOG_LIMIT = 20;

export function emptyDebugState(): DebugState {
  return { manifests: new Map(), cache: new Map(), log: [] };
}

/**
 * The newest cached answer to one call in a site, or undefined.
 *
 * Prefers the RETAINED entry over anything in the log, because the retained one is what
 * every other panel is seeing too — the cache is shared, so one operator's refresh
 * updates everybody. Reading the log first would show this tab its own older answer
 * while the rest of the team had a newer one.
 */
export function cachedAnswer(
  state: DebugState, siteId: string, call: string,
): CacheRecord | undefined {
  let best: CacheRecord | undefined;
  for (const rec of state.cache.values()) {
    if (rec.siteId !== siteId || rec.call !== call) continue;
    if (!best || rec.stored_at > best.stored_at) best = rec;
  }
  return best;
}

/**
 * Fold one debug-channel message into the state. Returns true if anything changed.
 *
 * Unknown channels are ignored rather than rejected: this is called for every message
 * and most are telemetry.
 */
export function applyDebugMessage(
  state: DebugState,
  siteId: string,
  name: ChannelName,
  value: unknown,
  /** The `{entryId}` from the topic, for `fleetDebugCache`. One topic per entry, so the
   *  id is in the address rather than the payload. */
  entryId?: string,
): boolean {
  if (name === "fleetDebugManifest") {
    const m = value as DebugManifest;
    if (!m || !Array.isArray(m.calls)) return false;
    state.manifests.set(siteId, m);
    return true;
  }
  if (name === "fleetDebugCache") {
    const rec = value as Partial<CacheRecord>;
    // An empty retained payload is how MQTT DELETES a retained message, so it arrives
    // here as a clear rather than as an entry — the adapter publishes one when it sweeps
    // an orphan. Treating it as data would resurrect exactly what was being removed.
    if (!rec || typeof rec.call !== "string" || typeof rec.stored_at !== "number") {
      state.cache.delete(`${siteId}|${entryId ?? ""}`);
      return true;
    }
    state.cache.set(`${siteId}|${entryId ?? rec.call}`, {
      siteId, entryId: entryId ?? rec.call, call: rec.call,
      params: rec.params, data: rec.data,
      stored_at: rec.stored_at, ttl_ms: Number(rec.ttl_ms ?? 0), tier: rec.tier,
    });
    return true;
  }
  if (name === "fleetDebugResult") {
    const r = value as DebugResult;
    if (!r?.request_id) return false;
    const hit = state.log.find((p) => p.requestId === r.request_id);
    if (!hit) {
      // An answer to a question this tab did not ask — another operator's, or ours from
      // before a reload. Recorded rather than dropped: seeing someone else's query is
      // the point of a shared channel, and silently discarding it would make the panel
      // look broken to whoever DID ask.
      state.log.unshift({
        requestId: r.request_id, call: r.call ?? "(elsewhere)", siteId,
        sentAt: r.ts * 1000, result: r,
      });
      state.log.length = Math.min(state.log.length, LOG_LIMIT);
      return true;
    }
    hit.result = r;
    return true;
  }
  return false;
}

/**
 * Resolved through the contract binding, which names the CHANNEL and lets the generated
 * table supply the address. (This used to hand-build `sites/${siteId}/cmd/debug_query`
 * and carry a comment admitting it; `src/contract/fleetComms.ts` is the fix.)
 */
export const queryTopic = (siteId: string) => fleetTopic(siteId, "cmdDebugQuery");

let seq = 0;

/** Unique per tab and per request. `crypto.randomUUID` is not assumed — it needs a
 *  secure context, and a plain-http dev origin would break the whole panel. */
export function newRequestId(): string {
  seq += 1;
  return `web-${Date.now().toString(36)}-${seq}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Coerce a form value to what the parameter's schema asks for.
 *
 * A form gives strings. Sending "3" where the adapter validates an integer earns an
 * `invalid_params` round trip that says nothing the panel could not have known.
 */
export function coerce(raw: string, schema: ParamSchema | undefined): unknown {
  const t = schema?.type;
  if (t === "integer" || t === "number") {
    const n = Number(raw);
    return Number.isFinite(n) ? n : raw;   // let the adapter explain a non-number
  }
  if (t === "boolean") return raw === "true" || raw === "1";
  return raw;
}

/** What is missing before this is worth sending. Empty = go. */
export function missingRequired(call: DebugCall, values: Record<string, string>): string[] {
  return (call.required ?? []).filter((n) => !String(values[n] ?? "").trim());
}

// ---- freshness and the courtesy cooldown -------------------------------------

/** Whether the ADAPTER says this call's answers may be cached. Unknown tier, missing
 *  block, or `none` all mean no — an old adapter and an uncacheable call are different
 *  facts, but neither licenses us to assume an answer is reusable. */
export function isCacheable(call: DebugCall): boolean {
  const ttl = call.cache?.ttl_ms;
  return typeof ttl === "number" && ttl > 0;
}

/**
 * How a cached answer should read to a human. Derived from the answer itself — `cached`
 * and `age_ms` are in the payload — so it needs no client-side bookkeeping and stays
 * true in a replay.
 *
 * `(stale)` is not a failure: expiry means "refetch when next asked", never "delete", so
 * a stale value still beats a blank panel when the vendor is unreachable.
 */
export function freshness(call: DebugCall | undefined, r: DebugResult): string {
  if (!r.cached) return "";
  const age = typeof r.age_ms === "number" ? r.age_ms : 0;
  const ttl = call?.cache?.ttl_ms ?? 0;
  return `cached ${humanAge(age)}${ttl > 0 && age >= ttl ? " (stale)" : ""}`;
}

/** Largest whole unit that does not round to zero: "40s", "4m", "2d". An age shown as
 *  "0h" reads as "just now", which is the opposite of what a day-old answer means. */
export function humanAge(ms: number): string {
  for (const [size, suffix] of [[86_400_000, "d"], [3_600_000, "h"], [60_000, "m"]] as const) {
    if (ms >= size) return `${Math.floor(ms / size)}${suffix} ago`;
  }
  return `${Math.max(0, Math.floor(ms / 1000))}s ago`;
}

/** When each call was last actually asked of the vendor, for the courtesy cooldown.
 *  Per tab and in memory: the adapter remains the enforcement, and this only exists so a
 *  button can explain itself before being pressed. */
const lastAsked = new Map<string, number>();

export function cooldownRemainingMs(call: DebugCall, now: number): number {
  const min = call.min_interval_ms ?? 0;
  const last = lastAsked.get(call.name);
  if (!min || last === undefined) return 0;
  return Math.max(0, min - (now - last));
}

export interface SendOutcome {
  pending?: Pending;
  error?: string;
}

/**
 * Publish one query and record it as pending.
 *
 * The pending entry is created BEFORE the publish is confirmed, so a result that beats
 * this function's return still finds its row. A failed publish removes it again.
 */
export function sendQuery(
  conn: Connection,
  state: DebugState,
  siteId: string,
  call: DebugCall,
  values: Record<string, string>,
  robotId?: string,
  force = false,
): SendOutcome {
  const missing = missingRequired(call, values);
  if (missing.length) return { error: `missing: ${missing.join(", ")}` };

  // A COURTESY mirror of the adapter's rate limit, never the enforcement — the adapter
  // decides, and it is the only thing that can, since other operators are asking too.
  // The point is that a greyed button explains itself where a `throttled` round trip
  // does not. Skipped when the answer would come from cache anyway: a cached answer
  // costs the vendor nothing, so refusing it would be protecting a credential from a
  // request that was never going to reach the vendor. (Same reasoning, same order, as
  // the adapter's own check — see `_run_debug_query`.)
  if (force || !isCacheable(call)) {
    const wait = cooldownRemainingMs(call, Date.now());
    if (wait > 0) return { error: `too soon — ${Math.ceil(wait / 1000)}s` };
  }

  const params: Record<string, unknown> = {};
  for (const [name, schema] of Object.entries(call.params ?? {})) {
    const raw = String(values[name] ?? "").trim();
    if (raw !== "") params[name] = coerce(raw, schema);
  }

  const pending: Pending = {
    requestId: newRequestId(), call: call.name, siteId, sentAt: Date.now(),
    ...(robotId ? { robotId } : {}),
    ...(force ? { forced: true } : {}),
  };
  // Recorded only when the vendor might actually be reached, for the same reason the
  // check above is skipped for a cache hit.
  if (force || !isCacheable(call)) lastAsked.set(call.name, pending.sentAt);
  state.log.unshift(pending);
  state.log.length = Math.min(state.log.length, LOG_LIMIT);

  const err = conn.publish(queryTopic(siteId), {
    request_id: pending.requestId,
    call: call.name,
    ...(Object.keys(params).length ? { params } : {}),
    // Omitted unless true: an older adapter has a closed schema for this payload, and a
    // `force: false` it has never heard of would be rejected outright.
    ...(force ? { force: true } : {}),
  }, "cmdDebugQuery");
  if (err) {
    state.log = state.log.filter((p) => p !== pending);
    lastAsked.delete(call.name);        // it never went; do not hold a cooldown for it
    return { error: err };
  }
  return { pending };
}

/**
 * Re-ask one call with `force: true`, reusing THE PARAMETERS THAT PRODUCED THE ANSWER
 * rather than whatever is currently typed in the form.
 *
 * That distinction is the whole correctness of the button. A refresh means "this exact
 * answer again, fresh"; if it took the live form values it could quietly ask a different
 * question and replace the answer on screen with one about something else — and the row
 * would look like a refresh of what was there before. The result's `params` are the
 * canonical post-validation set, which is precisely what the cache entry is filed under,
 * so this addresses the same cache entry by construction.
 *
 * Scoped to the same robot (or to none), because a shared channel carries every
 * operator's questions and the newest matching row might not be ours to refresh.
 */
export function refreshQuery(
  conn: Connection,
  state: DebugState,
  siteId: string,
  call: DebugCall,
  robotId?: string,
): SendOutcome {
  const prior = state.log.find(
    (p) => p.call === call.name && p.siteId === siteId && p.robotId === robotId && p.result);
  if (!prior) return { error: `nothing to refresh for ${call.name}` };

  // Back to strings, because that is what `sendQuery` coerces from — the same path a
  // typed form takes, so one definition of coercion serves both.
  const values: Record<string, string> = {};
  for (const [k, v] of Object.entries(prior.result?.params ?? {})) values[k] = String(v);

  return robotId
    ? sendRobotQuery(conn, state, siteId, robotId, call, values, true)
    : sendQuery(conn, state, siteId, call, values, undefined, true);
}

// ---- `x-ui` pickers ----------------------------------------------------------
//
// CR 008 Phase 4 item 2. A picker turns "type 94:BA:06:CA:99:50 correctly" into "choose
// one", which is the single biggest source of a query that fails for a reason the
// operator cannot see — a mistyped serial is a valid string, so it earns a vendor error
// rather than a form error.
//
// EVERY OPTION COMES FROM STATE THE PANEL ALREADY HAS: the retained MQTT roster, or an
// answer already in the log (which, since step 2, is usually served from the shared
// cache). A picker therefore costs NO extra vendor calls — which matters, because the
// whole reason this surface is careful is that over-polling disables the client_id.

export interface PickerOption { value: string; label: string }

/**
 * Every object anywhere in a JSON tree that carries `key` as a scalar.
 *
 * Depth-agnostic on purpose: the vendor wraps list responses differently per endpoint —
 * sometimes a bare array, sometimes `{records: [...]}` with paging around it. Matching
 * one shape would work until the first endpoint that uses the other, and fail by finding
 * nothing, which is indistinguishable from an empty list. Same lesson as the adapter's
 * `nested_get`, which had to become depth-agnostic when the live C40 nested battery two
 * levels deep.
 */
export function objectsWith(data: unknown, key: string): Record<string, unknown>[] {
  const out: Record<string, unknown>[] = [];
  const seen = new Set<unknown>();
  const walk = (node: unknown) => {
    if (!node || typeof node !== "object" || seen.has(node)) return;
    seen.add(node);                                   // vendor payloads are JSON, but a
    if (Array.isArray(node)) { node.forEach(walk); return; }  // cycle here would hang the tab
    const rec = node as Record<string, unknown>;
    const v = rec[key];
    if (v !== undefined && v !== null && typeof v !== "object") out.push(rec);
    Object.values(rec).forEach(walk);
  };
  walk(data);
  return out;
}

/**
 * The newest answer to one call in this site, from EITHER source, or undefined.
 *
 * The retained cache is tried first and is usually the only one that exists: it arrives
 * on subscribe, so a panel that has just opened can already populate a picker without
 * anybody having asked a question. The log is the fallback for calls that are not cached
 * at all (`clean_log`, the live readings) and for an adapter too old to publish entries.
 */
function newestAnswer(state: DebugState, siteId: string, call: string): unknown {
  const cached = cachedAnswer(state, siteId, call);
  if (cached) return cached.data;
  return state.log.find((p) => p.call === call && p.siteId === siteId && p.result?.ok)
              ?.result?.data;
}

/**
 * Options for one `x-ui` hint, or an empty list when there is nothing to offer yet.
 *
 * An empty list is not a failure — it means "nobody has fetched the store list in this
 * session". The caller degrades to a plain text input, exactly as it does for an
 * unrecognised hint, because a picker with no options is a dead end where a text box is
 * merely inconvenient.
 *
 * `robotIds` comes from the retained roster rather than from `robot_list`: it arrives
 * free on subscribe and is the same data. Asking the vendor for what MQTT already
 * delivered is the habit this whole phase exists to break.
 */
export function pickerOptions(
  hint: string | undefined,
  state: DebugState,
  siteId: string,
  robotIds: string[],
): PickerOption[] {
  if (hint === "robot-picker") {
    return robotIds.map((id) => ({ value: id, label: id }));
  }
  if (hint === "store-picker") {
    return objectsWith(newestAnswer(state, siteId, "store_list"), "storeId")
      .map((o) => ({ value: String(o.storeId), label: labelFor(o, "storeName", o.storeId) }));
  }
  if (hint === "scene-picker") {
    return objectsWith(newestAnswer(state, siteId, "scene_list"), "sceneCode")
      .map((o) => ({ value: String(o.sceneCode), label: labelFor(o, "sceneName", o.sceneCode) }));
  }
  return [];               // unknown hint: the caller falls back to a plain input
}

/** `Name (id)` when the vendor gave a name, otherwise just the id. Both are shown
 *  because the id is what goes on the wire and what an error message will quote. */
function labelFor(o: Record<string, unknown>, nameKey: string, id: unknown): string {
  const name = o[nameKey];
  return typeof name === "string" && name.trim() ? `${name} (${String(id)})` : String(id);
}

/** Calls grouped for display, in the manifest's own order within each group. */
export function byGroup(m: DebugManifest): Array<[string, DebugCall[]]> {
  const groups = new Map<string, DebugCall[]>();
  for (const c of m.calls) {
    const g = c.group ?? "other";
    if (!groups.has(g)) groups.set(g, []);
    groups.get(g)!.push(c);
  }
  return [...groups.entries()];
}

// ---- which calls belong to a robot ------------------------------------------
//
// DERIVED, never listed. The manifest already says this twice over — `scope` names what
// a call is addressed by, and any robot parameter carries `x-ui: "robot-picker"` — so a
// call added to the adapter lands in the right view with no change here. A hardcoded
// list of "robot calls" in this app would be a second declaration of the registry, and
// would be wrong the first time a call was added.

/** The parameter this call takes a robot in, or null if it takes none. */
export function robotParam(call: DebugCall): string | null {
  for (const [name, schema] of Object.entries(call.params ?? {})) {
    // The hint is the strongest signal: it is exactly what `x-ui` is for.
    if (schema["x-ui"] === "robot-picker") return name;
  }
  // Fall back to `scope`, which says the same thing less specifically. Both are checked
  // because an adapter might declare one and not the other, and getting this wrong
  // hides a call rather than merely mis-styling it.
  if (call.scope === "robot") {
    const names = Object.keys(call.params ?? {});
    return names.find((n) => /robot/i.test(n)) ?? names[0] ?? null;
  }
  return null;
}

export const isRobotScoped = (call: DebugCall): boolean => robotParam(call) !== null;

/** Calls for the fleet view: everything NOT addressed to one robot. */
export function fleetCalls(m: DebugManifest): DebugCall[] {
  return m.calls.filter((c) => !isRobotScoped(c));
}

/** Calls for a robot's detail view. */
export function robotCalls(m: DebugManifest): DebugCall[] {
  return m.calls.filter(isRobotScoped);
}

/**
 * Send a robot-scoped query with the robot filled in from the selection.
 *
 * The operator never types a serial in the detail view: they are already looking at one
 * robot, and retyping `AA:BB:CC:00:00:01` into a form is both tedious and the likeliest
 * way to query the wrong machine. Any OTHER parameter the call declares is still asked
 * for.
 */
export function sendRobotQuery(
  conn: Connection,
  state: DebugState,
  siteId: string,
  robotId: string,
  call: DebugCall,
  values: Record<string, string>,
  force = false,
): SendOutcome {
  const param = robotParam(call);
  if (!param) return { error: `${call.name} is not addressed to a robot` };
  return sendQuery(conn, state, siteId, call, { ...values, [param]: robotId }, robotId,
                   force);
}
